import { ButtonHTMLAttributes, useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import {
  PRIMARY_DARK_GREEN,
  SECONDARY_DARK_GREEN,
} from "../../constants/color";
import { IconComponent } from "./icons/types";
import { PC_BREAKPOINT } from "../../constants/app";

export type ButtonVariant = "filled" | "border" | "dark" | "link";

type Props = {
  uuid?: string;
  text: string;
  variant: ButtonVariant;
  type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  onClick?: () => void;
  disabled?: boolean;
  fullWidth?: boolean;
  icon?: IconComponent;
};

export const Button: React.FC<Props> = ({
  uuid,
  text,
  variant,
  type,
  onClick,
  disabled,
  fullWidth,
  icon: Icon,
}) => {
  const formContext = useFormContext();
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const handleOnClick = useCallback(() => {
    if (disabled) return;

    onClick?.();
  }, [disabled, onClick]);

  const iconColor = useMemo(() => {
    switch (variant) {
      case "filled":
        return "#ffffff";
      case "border":
        return isHovered ? SECONDARY_DARK_GREEN : PRIMARY_DARK_GREEN;
      case "dark":
        return isHovered ? PRIMARY_DARK_GREEN : "#000000";
      case "link":
        return isHovered ? SECONDARY_DARK_GREEN : PRIMARY_DARK_GREEN;
      default:
        return "#333333";
    }
  }, [variant, isHovered]);

  return (
    <StyledButton
      id={uuid ? `button-${uuid}` : undefined}
      $variant={variant}
      type={type}
      onClick={handleOnClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={
        type === "submit"
          ? !formContext?.formState.isValid ||
            formContext?.formState.isSubmitting
          : disabled
      }
      $fullWidth={fullWidth}
    >
      {Icon && <Icon fillColor={iconColor} />} {text}
    </StyledButton>
  );
};

const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $fullWidth?: boolean;
}>`
  ${({ $fullWidth }) => $fullWidth && "width: 100%;"}
  min-width: 100px;
  height: ${({ $variant }) => ($variant === "link" ? "40px" : "48px")};
  padding: ${({ $variant }) =>
    $variant === "link" ? "10px 14px" : "14px 24px"};
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  text-align: center;
  font-size: 14px;
  font-weight: ${({ $variant }) =>
    $variant === "filled" || $variant === "border" ? "700" : "400"};
  line-height: 100%;
  cursor: pointer;
  transition: background-color 0.3s;
  ${({ $variant }) => {
    switch ($variant) {
      case "filled":
        return `
          background-color: ${PRIMARY_DARK_GREEN};
          color: #ffffff;
          border: none;
        `;
      case "border":
        return `
          background-color: #ffffff;
          color: ${PRIMARY_DARK_GREEN};
          border: 1px solid ${PRIMARY_DARK_GREEN};
        `;
      case "dark":
        return `
            background-color: #ffffff;
            color: #000000;
            border: 1px solid #999999;
          `;
      case "link":
        return `
          background-color: transparent;
          color: ${PRIMARY_DARK_GREEN};
          border: none;
          text-decoration: underline;
        `;
    }
  }}

  &:hover {
    ${({ $variant }) => {
      switch ($variant) {
        case "filled":
          return `
            @media (min-width: ${PC_BREAKPOINT}) {
              background-color: ${SECONDARY_DARK_GREEN};
            }
          `;
        case "border":
          return `
            @media (min-width: ${PC_BREAKPOINT}) {
              background-color: #ffffff;
              color: ${SECONDARY_DARK_GREEN};
              border-color: ${SECONDARY_DARK_GREEN};
            }
          `;
        case "dark":
          return `
            @media (min-width: ${PC_BREAKPOINT}) {
              background-color: #ffffff;
              color: ${PRIMARY_DARK_GREEN};
              border-color: ${PRIMARY_DARK_GREEN};
            }
          `;
        case "link":
          return `
            @media (min-width: ${PC_BREAKPOINT}) {
              color: ${SECONDARY_DARK_GREEN};
              text-decoration: none;
            }
          `;
      }
    }};
  }

  &:disabled {
    cursor: not-allowed;
    ${({ $variant }) => {
      switch ($variant) {
        case "filled":
          return `
            background-color: #cccccc;
            border-color: #999999;
            color: #f2f2f2;
          `;
        case "border":
        case "dark":
          return `
            background-color: #f2f2f2;
            border-color: #cccccc;
            color: #999999;
          `;
        case "link":
          return `
            color: #999999;
            text-decoration: none;
          `;
      }
    }}
  }
`;
